import { useEffect, useState, useCallback } from "react";
import { useParams, Link, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { formatINR, formatDate, STATUS_TONE } from "@/lib/format";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, BadgeCheck, AlertTriangle, Edit3 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const RET_HEADS = [
  "Retention PO Terms", "IR Retention", "Safety Retention",
  "BG Retention", "GST Retention", "Other Retention",
];

const Section = ({ title, children, right }) => (
  <div className="border-b border-slate-200 py-6">
    <div className="flex items-center justify-between mb-4">
      <h3 className="font-display text-lg font-semibold text-slate-900">{title}</h3>
      {right}
    </div>
    {children}
  </div>
);

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</div>
    <div className="text-sm text-slate-900 mt-1">{value ?? "—"}</div>
  </div>
);

export default function InvoiceDetail() {
  const { id } = useParams();
  const location = useLocation();
  const backTo = location.state?.from || "/invoices";
  const backLabel = location.state?.fromLabel || "Back to Invoices";
  const showRetentions = backTo === "/retentions";
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [statusOpts, setStatusOpts] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [retOpen, setRetOpen] = useState(false);
  const [reqOpen, setReqOpen] = useState(false);
  const [reqEditId, setReqEditId] = useState(null);
  const [payForm, setPayForm] = useState({ amount: "", payment_date: "", mode: "", reference: "", remark: "" });
  const [retForm, setRetForm] = useState({ head: RET_HEADS[0], request_number: "", request_date: "", amount: "", remark: "" });
  const [reqForm, setReqForm] = useState({ head: RET_HEADS[0], request_number: "", request_date: "", amount: "", remark: "" });
  const [jobsheet, setJobsheet] = useState({});
  const [ld, setLd] = useState({ ld_amount: 0, remark: "" });
  const [cess, setCess] = useState(0);

  const load = useCallback(async () => {
    const { data } = await api.get(`/invoices/${id}`);
    setData(data);
    setJobsheet(data.invoice.jobsheet || {});
    setLd({ ld_amount: data.invoice.ld_amount || 0, remark: data.invoice.ld_remark || "" });
    setCess(data.invoice.cess_amount || 0);
  }, [id]);

  useEffect(() => {
    load();
    api.get("/status-options").then(({ data }) => setStatusOpts(data));
  }, [load]);

  if (!data) return <div className="p-12 text-sm text-slate-500">Loading…</div>;
  const inv = data.invoice;

  const addPayment = async () => {
    try {
      await api.post(`/invoices/${id}/payments`, {
        ...payForm,
        amount: Number(payForm.amount || 0),
      });
      toast.success("Payment recorded");
      setPayOpen(false);
      setPayForm({ amount: "", payment_date: "", mode: "", reference: "", remark: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const delPayment = async (pid) => {
    if (!window.confirm("Delete this payment receipt?")) return;
    await api.delete(`/payments/${pid}`);
    toast.success("Deleted");
    load();
  };

  const addRetention = async () => {
    try {
      await api.post(`/invoices/${id}/retentions`, {
        ...retForm,
        amount: Number(retForm.amount || 0),
      });
      toast.success("Retention request added");
      setRetOpen(false);
      setRetForm({ head: RET_HEADS[0], request_number: "", request_date: "", amount: "", remark: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const payRetention = async (ret) => {
    const paid = window.prompt(`Enter paid amount for ${ret.head} (request ${ret.request_number || "-"}, due ${ret.amount}):`, ret.amount);
    if (paid == null) return;
    const date = window.prompt("Payment date (YYYY-MM-DD), leave blank for today:") || "";
    await api.put(`/retentions/${ret.id}/pay`, { paid_amount: Number(paid), paid_date: date, remark: "" });
    toast.success("Retention marked paid");
    load();
  };

  const delRetention = async (rid) => {
    if (!window.confirm("Delete this retention request?")) return;
    await api.delete(`/retentions/${rid}`);
    toast.success("Deleted");
    load();
  };

  const addReleaseRequest = async () => {
    try {
      const payload = { ...reqForm, amount: Number(reqForm.amount || 0) };
      if (reqEditId) {
        await api.put(`/retention-requests/${reqEditId}`, payload);
        toast.success("Release request updated");
      } else {
        await api.post(`/invoices/${id}/retention-requests`, payload);
        toast.success("Release request raised");
      }
      setReqOpen(false);
      setReqEditId(null);
      setReqForm({ head: RET_HEADS[0], request_number: "", request_date: "", amount: "", remark: "" });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const startEditRequest = (req) => {
    setReqForm({
      head: req.head,
      request_number: req.request_number || "",
      request_date: req.request_date || "",
      amount: String(req.amount ?? ""),
      remark: req.remark || "",
    });
    setReqEditId(req.id);
    setReqOpen(true);
  };

  const startCreateRequest = () => {
    setReqForm({ head: RET_HEADS[0], request_number: "", request_date: "", amount: "", remark: "" });
    setReqEditId(null);
    setReqOpen(true);
  };

  const payReleaseRequest = async (req) => {
    const date = window.prompt(
      `Mark request ${req.request_number} (${req.head}, ₹${req.amount.toLocaleString("en-IN")}) as PAID.\nPayment date (YYYY-MM-DD), leave blank for today:`,
      ""
    );
    if (date == null) return;
    try {
      await api.put(`/retention-requests/${req.id}/pay`, { paid_date: date });
      toast.success("Release request marked Paid & retention released");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const delReleaseRequest = async (rid) => {
    if (!window.confirm("Delete this release request?")) return;
    try {
      await api.delete(`/retention-requests/${rid}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  const saveJobsheet = async () => {
    try {
      await api.put(`/invoices/${id}/jobsheet`, jobsheet);
      toast.success("Jobsheet/SES updated");
      load();
    } catch (e) {
      toast.error("Failed");
    }
  };

  const saveLD = async () => {
    await api.put(`/invoices/${id}/ld`, { ld_amount: Number(ld.ld_amount || 0), remark: ld.remark });
    toast.success("LD updated");
    load();
  };

  const saveCESS = async () => {
    await api.put(`/invoices/${id}/cess`, { cess_amount: Number(cess || 0) });
    toast.success("CESS updated");
    load();
  };

  const setStatus = async (status) => {
    await api.put(`/invoices/${id}/status`, { status });
    toast.success("Status updated");
    load();
  };

  return (
    <div className="p-4 sm:p-6 lg:p-12 max-w-[1400px]">
      <Link to={backTo} className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-slate-500 hover:text-slate-900 mb-4" data-testid="back-invoices">
        <ArrowLeft className="w-3.5 h-3.5" /> {backLabel}
      </Link>

      {/* Header */}
      <div className="flex items-start justify-between gap-6 flex-wrap mb-8">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.3em] text-slate-400">Invoice</div>
          <h1 className="font-display text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mt-1">
            {inv.invoice_number}
          </h1>
          <div className="text-sm text-slate-500 mt-2">
            {inv.party_client_name ? <span className="font-medium text-slate-700">{inv.party_client_name} · </span> : null}
            {inv.subvendor_name || "—"} · {inv.project || "—"} · {inv.fy || "—"}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {showRetentions ? (
            <span className={`status-pill ${STATUS_TONE(inv.status)}`}>{inv.status}</span>
          ) : (
            <>
              <Select value={inv.status} onValueChange={setStatus}>
                <SelectTrigger className="rounded-none w-72 h-10" data-testid="status-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOpts
                    .filter((s) => s.active !== false || s.label === inv.status)
                    .map((s) => (
                      <SelectItem key={s.id} value={s.label}>
                        {s.label}{s.active === false ? " (inactive)" : ""}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              <span className={`status-pill ${STATUS_TONE(inv.status)}`}>{inv.status}</span>
            </>
          )}
        </div>
      </div>

      {/* Net Payable highlight box */}
      <div className="grid grid-cols-1 md:grid-cols-4 mb-8">
        <div className="bg-slate-900 text-white p-6">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-white/60">Net Payable</div>
          <div className="font-display text-3xl font-bold mt-2 font-mono-data">{formatINR(inv.net_payable)}</div>
          <div className="text-[10px] text-white/60 mt-1">After TDS, CESS & LD</div>
        </div>
        <div className="bg-white border border-l-0 border-slate-200 p-6">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Total Paid</div>
          <div className="font-display text-2xl font-bold text-emerald-700 mt-2 font-mono-data">{formatINR(inv.total_paid)}</div>
        </div>
        <div className="bg-white border border-l-0 border-slate-200 p-6">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Retention Held</div>
          <div className="font-display text-2xl font-bold text-blue-700 mt-2 font-mono-data">{formatINR(inv.pending_retention)}</div>
        </div>
        <div className="bg-white border border-l-0 border-slate-200 p-6">
          <div className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Outstanding</div>
          <div className={`font-display text-2xl font-bold mt-2 font-mono-data ${inv.outstanding > 0 ? "text-amber-700" : "text-slate-700"}`}>{formatINR(inv.outstanding)}</div>
        </div>
      </div>

      <Tabs defaultValue="overview" className="bg-white border border-slate-200">
        <TabsList className="rounded-none bg-slate-50 border-b border-slate-200 h-12 px-0 justify-start w-full">
          <TabsTrigger value="overview" data-testid="tab-overview" className="rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none px-5">Overview</TabsTrigger>
          <TabsTrigger value="jobsheet" data-testid="tab-jobsheet" className="rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none px-5">Jobsheet / SES</TabsTrigger>
          <TabsTrigger value="payments" data-testid="tab-payments" className="rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none px-5">Payments</TabsTrigger>
          {showRetentions && (
            <TabsTrigger value="retentions" data-testid="tab-retentions" className="rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none px-5">Retentions</TabsTrigger>
          )}
          <TabsTrigger value="ld" data-testid="tab-ld" className="rounded-none data-[state=active]:bg-white data-[state=active]:border-b-2 data-[state=active]:border-[#002FA7] data-[state=active]:shadow-none px-5">LD &amp; CESS</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="px-6 py-2">
          <Section title="Invoice Particulars">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5">
              <Field label="Party / Client" value={inv.party_client_name} />
              <Field label="Subvendor Name" value={inv.subvendor_name} />
              <Field label="Invoice Date" value={formatDate(inv.invoice_date)} />
              <Field label="Service Period" value={inv.invoice_service_period} />
              <Field label="RC Number" value={inv.rc_number} />
              <Field label="RC Amount" value={formatINR(inv.rc_amount)} />
              <Field label="RC Start" value={formatDate(inv.rc_start_date)} />
              <Field label="RC End" value={formatDate(inv.rc_end_date)} />
              <Field label="RO No" value={inv.ro_no} />
              <Field label="RO Date" value={formatDate(inv.ro_date)} />
              <Field label="Description" value={inv.invoice_description} />
            </div>
          </Section>
          <Section title="Amount Breakdown">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-5">
              <Field label="Base Amount" value={formatINR(inv.base_amount)} />
              <Field label="GST Amount" value={formatINR(inv.gst_amount)} />
              <Field label="Total (Net) Amount" value={formatINR(inv.net_amount)} />
              <Field label="TDS (2%)" value={formatINR(inv.tds_amount)} />
              <Field label="CESS" value={formatINR(inv.cess_amount)} />
              <Field label="LD Amount" value={formatINR(inv.ld_amount)} />
              <Field label="Net Payable" value={formatINR(inv.net_payable)} />
              <Field label="Outstanding" value={formatINR(inv.outstanding)} />
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="jobsheet" className="px-6 py-2">
          <Section title="Jobsheet & SES Details" right={
            hasPerm("jobsheet") && (
              <Button onClick={saveJobsheet} data-testid="save-jobsheet" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none">Save</Button>
            )
          }>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-5">
              {[
                ["jobsheet_number", "Jobsheet Number", "text"],
                ["jobsheet_upload_date", "Jobsheet Upload Date", "date"],
                ["jobsheet_final_approval_date", "Jobsheet Final Approval Date", "date"],
                ["invoice_upload_date", "Invoice Upload Date", "date"],
                ["ses_approval_date", "SES Approval Date", "date"],
              ].map(([k, label, type]) => (
                <div key={k}>
                  <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">{label}</Label>
                  <Input
                    type={type}
                    value={jobsheet[k] || ""}
                    onChange={(e) => setJobsheet({ ...jobsheet, [k]: e.target.value })}
                    className="rounded-none mt-1"
                    data-testid={`js-${k}`}
                    disabled={!hasPerm("jobsheet")}
                  />
                </div>
              ))}
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">
                  Payment Due Date {inv.payment_due_is_override ? "(manual override)" : "(auto)"}
                </Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    type="date"
                    value={jobsheet.payment_due_date_override || inv.payment_due_date || ""}
                    onChange={(e) => setJobsheet({ ...jobsheet, payment_due_date_override: e.target.value })}
                    className="rounded-none flex-1"
                    data-testid="payment-due-date"
                    disabled={!hasPerm("jobsheet")}
                  />
                  {hasPerm("jobsheet") && jobsheet.payment_due_date_override && (
                    <Button
                      type="button"
                      variant="outline"
                      className="rounded-none text-xs px-2"
                      onClick={() => setJobsheet({ ...jobsheet, payment_due_date_override: "" })}
                      data-testid="reset-payment-due-date"
                      title="Reset to auto (SES + 30 days)"
                    >Reset</Button>
                  )}
                </div>
                <div className="text-[10px] text-slate-400 mt-1">
                  {inv.payment_due_is_override ? "Manual override active. Reset to use SES + 30 days." : "Auto-calculated as SES Approval Date + 30 days. Edit to override."}
                </div>
              </div>
            </div>
          </Section>
        </TabsContent>

        <TabsContent value="payments" className="px-6 py-2">
          <Section title={`Payment Receipts (${data.payments.length})`} right={
            hasPerm("payments") && (
              <Dialog open={payOpen} onOpenChange={setPayOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-payment-btn" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none">
                    <Plus className="w-4 h-4 mr-1.5" /> Record Payment
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                  <DialogHeader><DialogTitle className="font-display">Record Payment</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div><Label>Amount *</Label><Input type="number" value={payForm.amount} onChange={(e)=>setPayForm({...payForm, amount:e.target.value})} className="rounded-none" data-testid="pay-amount" /></div>
                    <div><Label>Payment Receipt Date</Label><Input type="date" value={payForm.payment_date} onChange={(e)=>setPayForm({...payForm, payment_date:e.target.value})} className="rounded-none" data-testid="pay-date" /></div>
                    <div><Label>Mode</Label><Input value={payForm.mode} onChange={(e)=>setPayForm({...payForm, mode:e.target.value})} className="rounded-none" placeholder="NEFT/RTGS/UPI" data-testid="pay-mode" /></div>
                    <div><Label>Reference / UTR</Label><Input value={payForm.reference} onChange={(e)=>setPayForm({...payForm, reference:e.target.value})} className="rounded-none" data-testid="pay-ref" /></div>
                    <div><Label>Remark</Label><Textarea value={payForm.remark} onChange={(e)=>setPayForm({...payForm, remark:e.target.value})} className="rounded-none" data-testid="pay-remark" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={()=>setPayOpen(false)} className="rounded-none">Cancel</Button>
                    <Button onClick={addPayment} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="submit-payment">Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )
          }>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Date</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Mode</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Reference</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Remark</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.payments.length === 0 && <tr><td colSpan={6} className="text-center text-sm text-slate-400 py-10">No payments yet.</td></tr>}
                  {data.payments.map((p) => (
                    <tr key={p.id} className="border-b border-slate-100">
                      <td className="px-4 py-3">{formatDate(p.payment_date)}</td>
                      <td className="px-4 py-3 text-right font-mono-data font-semibold">{formatINR(p.amount)}</td>
                      <td className="px-4 py-3">{p.mode || "—"}</td>
                      <td className="px-4 py-3">{p.reference || "—"}</td>
                      <td className="px-4 py-3 text-slate-500">{p.remark || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {hasPerm("payments") && (
                          <button onClick={() => delPayment(p.id)} className="text-red-500 hover:text-red-700" data-testid={`del-pay-${p.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </TabsContent>

        {showRetentions && (
        <TabsContent value="retentions" className="px-6 py-2">
          <Section title={`Retention Holds (${data.retentions.length})`} right={
            hasPerm("retentions") && (
              <Dialog open={retOpen} onOpenChange={setRetOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="add-retention-btn" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none">
                    <Plus className="w-4 h-4 mr-1.5" /> Hold Retention
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                  <DialogHeader><DialogTitle className="font-display">Hold New Retention</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <Label>Head</Label>
                      <Select value={retForm.head} onValueChange={(v)=>setRetForm({...retForm, head:v})}>
                        <SelectTrigger className="rounded-none" data-testid="ret-head"><SelectValue /></SelectTrigger>
                        <SelectContent>{RET_HEADS.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div><Label>Reference Number</Label><Input value={retForm.request_number} onChange={(e)=>setRetForm({...retForm, request_number:e.target.value})} className="rounded-none" data-testid="ret-req-num" /></div>
                    <div><Label>Reference Date</Label><Input type="date" value={retForm.request_date} onChange={(e)=>setRetForm({...retForm, request_date:e.target.value})} className="rounded-none" data-testid="ret-req-date" /></div>
                    <div><Label>Amount *</Label><Input type="number" value={retForm.amount} onChange={(e)=>setRetForm({...retForm, amount:e.target.value})} className="rounded-none" data-testid="ret-amount" /></div>
                    <div><Label>Remark</Label><Textarea value={retForm.remark} onChange={(e)=>setRetForm({...retForm, remark:e.target.value})} className="rounded-none" data-testid="ret-remark" /></div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={()=>setRetOpen(false)} className="rounded-none">Cancel</Button>
                    <Button onClick={addRetention} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="submit-retention">Save</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )
          }>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Head</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Ref. No</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Ref. Date</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Released</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {data.retentions.length === 0 && <tr><td colSpan={7} className="text-center text-sm text-slate-400 py-10">No retention holds yet.</td></tr>}
                  {data.retentions.map((r) => (
                    <tr key={r.id} className="border-b border-slate-100">
                      <td className="px-4 py-3 font-medium">{r.head}</td>
                      <td className="px-4 py-3">{r.request_number || "—"}</td>
                      <td className="px-4 py-3">{formatDate(r.request_date)}</td>
                      <td className="px-4 py-3 text-right font-mono-data">{formatINR(r.amount)}</td>
                      <td className="px-4 py-3 text-right font-mono-data">{formatINR(r.paid_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`status-pill ${r.status === "Paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>{r.status}</span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        {hasPerm("retentions") && r.status !== "Paid" && (
                          <button onClick={() => payRetention(r)} className="text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`pay-ret-${r.id}`}>
                            <BadgeCheck className="w-4 h-4" /> Mark Paid
                          </button>
                        )}
                        {hasPerm("retentions") && (
                          <button onClick={() => delRetention(r.id)} className="text-red-500 hover:text-red-700" data-testid={`del-ret-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>

          <Section title={`Release Requests (${(data.release_requests || []).length})`} right={
            hasPerm("retentions") && (
              <Dialog
                open={reqOpen}
                onOpenChange={(o) => { setReqOpen(o); if (!o) setReqEditId(null); }}
              >
                <DialogTrigger asChild>
                  <Button onClick={startCreateRequest} data-testid="raise-request-btn" className="bg-[#002FA7] hover:bg-[#00227A] rounded-none">
                    <Plus className="w-4 h-4 mr-1.5" /> Raise Release Request
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-none">
                  <DialogHeader>
                    <DialogTitle className="font-display">
                      {reqEditId ? "Edit Retention Release Request" : "Raise Retention Release Request"}
                    </DialogTitle>
                  </DialogHeader>
                  <div className="bg-blue-50 border border-blue-200 text-[11px] text-blue-800 px-3 py-2 mb-2">
                    Formal request to the client to release a retention amount. When the client pays, mark this request Paid — the corresponding hold is auto-released (FIFO).
                  </div>
                  <div className="space-y-3">
                    <div>
                      <Label>Retention Head *</Label>
                      <Select value={reqForm.head} onValueChange={(v)=>setReqForm({...reqForm, head:v})}>
                        <SelectTrigger className="rounded-none" data-testid="req-head"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {RET_HEADS.map(h => {
                            const avail = inv.pending_by_head?.[h] || 0;
                            return (
                              <SelectItem key={h} value={h} disabled={avail <= 0 && !reqEditId}>
                                {h} {avail > 0 ? `· available ₹${avail.toLocaleString("en-IN")}` : "· nil"}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Retention Request Number *</Label>
                      <Input value={reqForm.request_number} onChange={(e)=>setReqForm({...reqForm, request_number:e.target.value})} className="rounded-none" data-testid="req-num" placeholder="e.g. RR/2026/001" />
                    </div>
                    <div>
                      <Label>Retention Request Date *</Label>
                      <Input type="date" value={reqForm.request_date} onChange={(e)=>setReqForm({...reqForm, request_date:e.target.value})} className="rounded-none" data-testid="req-date" />
                    </div>
                    <div>
                      <Label>Retention Request Amount *</Label>
                      <Input type="number" value={reqForm.amount} onChange={(e)=>setReqForm({...reqForm, amount:e.target.value})} className="rounded-none font-mono-data" data-testid="req-amount" />
                      <div className="text-[10px] text-slate-500 mt-1">Available for {reqForm.head}: ₹{(inv.pending_by_head?.[reqForm.head] || 0).toLocaleString("en-IN")}</div>
                    </div>
                    <div>
                      <Label>Remark</Label>
                      <Textarea value={reqForm.remark} onChange={(e)=>setReqForm({...reqForm, remark:e.target.value})} className="rounded-none" data-testid="req-remark" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={()=>{ setReqOpen(false); setReqEditId(null); }} className="rounded-none">Cancel</Button>
                    <Button onClick={addReleaseRequest} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="submit-request">
                      {reqEditId ? "Update Request" : "Raise Request"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            )
          }>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-y border-slate-200">
                  <tr>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Head</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Req. No</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Req. Date</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Amount</th>
                    <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Paid</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Remark</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {(data.release_requests || []).length === 0 && (
                    <tr><td colSpan={8} className="text-center text-sm text-slate-400 py-10">
                      No release requests raised yet. Click "Raise Release Request" to request a retention release from the client.
                    </td></tr>
                  )}
                  {(data.release_requests || []).map((r) => (
                    <tr key={r.id} className="border-b border-slate-100" data-testid={`req-row-${r.request_number}`}>
                      <td className="px-4 py-3 font-medium">{r.head}</td>
                      <td className="px-4 py-3 font-mono-data">{r.request_number}</td>
                      <td className="px-4 py-3">{formatDate(r.request_date)}</td>
                      <td className="px-4 py-3 text-right font-mono-data">{formatINR(r.amount)}</td>
                      <td className="px-4 py-3 text-right font-mono-data">{formatINR(r.paid_amount)}</td>
                      <td className="px-4 py-3">
                        <span className={`status-pill ${r.status === "Paid" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                          {r.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-slate-500 text-xs">{r.remark || "—"}</td>
                      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                        {hasPerm("retentions") && r.status !== "Paid" && (
                          <button onClick={() => startEditRequest(r)} className="text-slate-600 hover:text-[#002FA7] inline-flex items-center gap-1 text-xs font-semibold" data-testid={`edit-req-${r.id}`}>
                            <Edit3 className="w-3.5 h-3.5" /> Edit
                          </button>
                        )}
                        {hasPerm("retentions") && r.status !== "Paid" && (
                          <button onClick={() => payReleaseRequest(r)} className="text-emerald-600 hover:text-emerald-800 inline-flex items-center gap-1 text-xs font-semibold" data-testid={`pay-req-${r.id}`}>
                            <BadgeCheck className="w-4 h-4" /> Mark Paid
                          </button>
                        )}
                        {hasPerm("retentions") && r.status !== "Paid" && (
                          <button onClick={() => delReleaseRequest(r.id)} className="text-red-500 hover:text-red-700" data-testid={`del-req-${r.id}`}>
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        </TabsContent>
        )}

        <TabsContent value="ld" className="px-6 py-2">
          <Section title="LD Amount (Penalty, Non-Refundable)" right={
            hasPerm("ld") && (<Button onClick={saveLD} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="save-ld">Save LD</Button>)
          }>
            <div className="bg-red-50 border border-red-200 p-3 mb-4 flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5" />
              <div className="text-xs text-red-700">
                LD amount is a penalty deducted from net payable. Once entered, it adjusts the outstanding amount.
              </div>
            </div>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">LD Amount</Label>
                <Input
                  type="number"
                  value={ld.ld_amount}
                  onChange={(e)=>setLd({...ld, ld_amount: e.target.value})}
                  className="rounded-none mt-1"
                  disabled={!hasPerm("ld")}
                  data-testid="ld-amount-input"
                />
              </div>
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">Remark</Label>
                <Input
                  value={ld.remark}
                  onChange={(e)=>setLd({...ld, remark: e.target.value})}
                  className="rounded-none mt-1"
                  disabled={!hasPerm("ld")}
                  data-testid="ld-remark-input"
                />
              </div>
            </div>
          </Section>
          <Section title="CESS Amount" right={
            hasPerm("invoices") && (<Button onClick={saveCESS} className="bg-[#002FA7] hover:bg-[#00227A] rounded-none" data-testid="save-cess">Save CESS</Button>)
          }>
            <div className="grid grid-cols-2 gap-5">
              <div>
                <Label className="text-[10px] font-semibold uppercase tracking-[0.2em] text-slate-500">CESS Deducted by Client</Label>
                <Input
                  type="number"
                  value={cess}
                  onChange={(e)=>setCess(e.target.value)}
                  className="rounded-none mt-1"
                  disabled={!hasPerm("invoices")}
                  data-testid="cess-amount-input"
                />
              </div>
            </div>
          </Section>
        </TabsContent>
      </Tabs>
    </div>
  );
}
